import React, { useState, useEffect, useMemo, useRef } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import {
  Calculator, Upload, Download, Plus, FileText, X, Loader2,
  CheckCircle2, RotateCcw, Search, Edit3, Trash2, Eye, Calendar, UserCheck, Filter, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatPeriodMonth = (periodStr, yearNum, monthNum) => {
  if (periodStr && periodStr !== 'undefined-undefined' && periodStr.includes('-')) {
    const [y, m] = periodStr.split('-');
    const dateObj = new Date(Number(y), Number(m) - 1, 1);
    if (!isNaN(dateObj.getTime())) {
      return dateObj.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    }
    return periodStr;
  }
  if (yearNum && monthNum) {
    const dateObj = new Date(Number(yearNum), Number(monthNum) - 1, 1);
    if (!isNaN(dateObj.getTime())) {
      return dateObj.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    }
    return `${yearNum}-${String(monthNum).padStart(2, '0')}`;
  }
  return 'Not Assigned';
};

const ArrearsRevision = () => {
  const [tab, setTab] = useState('list');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  // Auto-calc form
  const [calc, setCalc] = useState({
    employee_id: '', new_ctc_annual: '', effective_from: '', effective_to: '',
    reason: 'CTC revision',
  });
  const [calcResult, setCalcResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Bulk upload
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ─── LOP Reversal Enhanced State ─────────────────────────────────────
  const [lopList, setLopList] = useState([]);
  const [lopSummary, setLopSummary] = useState(null);
  const [approvers, setApprovers] = useState([]);

  // Searchable Employee Selector state
  const [empSearchQuery, setEmpSearchQuery] = useState('');
  const [isEmpSearchOpen, setIsEmpSearchOpen] = useState(false);
  const searchRef = useRef(null);

  // Searchable Approver Selector state
  const [approverSearchQuery, setApproverSearchQuery] = useState('');
  const [isApproverSearchOpen, setIsApproverSearchOpen] = useState(false);
  const approverSearchRef = useRef(null);

  // Filters & Search state
  // Default to Current Payroll Month (YYYY-MM)
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7)); // e.g. "2026-07"
  const [gridSearchQuery, setGridSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [editingLop, setEditingLop] = useState(null);
  const [viewingLop, setViewingLop] = useState(null);
  const [deletingLopId, setDeletingLopId] = useState(null);

  const [lopForm, setLopForm] = useState({
    employee_id: '',
    source_year: new Date().getFullYear(),
    source_month: new Date().getMonth() + 1,
    effective_year: new Date().getFullYear(),
    effective_month: new Date().getMonth() + 1,
    processing_year: new Date().getFullYear(),
    processing_month: new Date().getMonth() + 1,
    lop_reversal_days: '',
    reason: '',
    remarks: '',
    approved_by: '',
    approved_by_role: '',
  });

  const [lopUploadResult, setLopUploadResult] = useState(null);
  const [lopUploading, setLopUploading] = useState(false);
  const [savingLop, setSavingLop] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([
        axios.get(`${API_URL}/payroll/arrears`, { withCredentials: true }),
        axios.get(`${API_URL}/employees`, { withCredentials: true }),
      ]);
      setList(a.data || []);
      setEmployees((e.data || []).filter((x) => x.employment_details?.is_active !== false));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load workspace data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLopData = async () => {
    try {
      const query = filterMonth ? `?payroll_processing_month=${filterMonth}` : '';
      const [res, appRes] = await Promise.all([
        axios.get(`${API_URL}/payroll/lop-reversals${query}`, { withCredentials: true }),
        axios.get(`${API_URL}/payroll/lop-reversals/approvers`, { withCredentials: true }),
      ]);

      const data = res.data;
      if (data && Array.isArray(data.records)) {
        setLopList(data.records);
        setLopSummary(data.summary || null);
      } else if (Array.isArray(data)) {
        setLopList(data);
        setLopSummary(null);
      } else {
        setLopList([]);
        setLopSummary(null);
      }
      setApprovers(appRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load LOP Reversal data');
      setLopList([]);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (tab === 'lop') {
      fetchLopData();
    }
  }, [tab, filterMonth]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsEmpSearchOpen(false);
      }
      if (approverSearchRef.current && !approverSearchRef.current.contains(e.target)) {
        setIsApproverSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered employees for intelligent search
  const filteredEmployees = useMemo(() => {
    if (!empSearchQuery.trim()) return employees.slice(0, 50);
    const q = empSearchQuery.toLowerCase().trim();
    return employees.filter((e) => {
      const code = (e.employment_details?.employee_code || '').toLowerCase();
      const fname = (e.personal_details?.first_name || '').toLowerCase();
      const lname = (e.personal_details?.last_name || '').toLowerCase();
      const mobile = (e.personal_details?.mobile_number || e.personal_details?.phone || '').toLowerCase();
      const dept = (e.employment_details?.department || '').toLowerCase();
      const desig = (e.employment_details?.designation || '').toLowerCase();
      return (
        code.includes(q) ||
        fname.includes(q) ||
        lname.includes(q) ||
        `${fname} ${lname}`.includes(q) ||
        mobile.includes(q) ||
        dept.includes(q) ||
        desig.includes(q)
      );
    }).slice(0, 50);
  }, [employees, empSearchQuery]);

  // Filtered approvers for role-based search
  const filteredApprovers = useMemo(() => {
    if (!approverSearchQuery.trim()) return approvers.slice(0, 40);
    const q = approverSearchQuery.toLowerCase().trim();
    return approvers.filter((a) => {
      const name = (a.employee_name || a.display_label || '').toLowerCase();
      const code = (a.employee_code || '').toLowerCase();
      const role = (a.role_name || '').toLowerCase();
      const dept = (a.department || '').toLowerCase();
      return name.includes(q) || code.includes(q) || role.includes(q) || dept.includes(q);
    }).slice(0, 40);
  }, [approvers, approverSearchQuery]);

  const selectedEmployeeObj = useMemo(() => {
    return employees.find((e) => e.id === lopForm.employee_id || e._id === lopForm.employee_id);
  }, [employees, lopForm.employee_id]);

  // Real-time Component-wise Arrear Preview calculation
  const lopPreviewBreakup = useMemo(() => {
    if (!selectedEmployeeObj || !lopForm.lop_reversal_days || Number(lopForm.lop_reversal_days) <= 0) {
      return { components: [], total: 0, baseDays: 30 };
    }

    const days = Number(lopForm.lop_reversal_days);
    const ey = Number(lopForm.effective_year || 2026);
    const em = Number(lopForm.effective_month || 1);
    const baseDays = new Date(ey, em, 0).getDate();

    const compDetails = selectedEmployeeObj.compensation_details || {};
    const components = compDetails.components || [];
    let breakup = [];
    let total = 0;

    if (components.length > 0) {
      components.forEach((c) => {
        const ht = (c.head_type || 'earning').toLowerCase();
        if (ht !== 'earning') return;
        const monthly = Number(c.monthly_amount || 0);
        if (monthly <= 0) return;
        const daily = monthly / baseDays;
        const arrear = Math.round(daily * days * 100) / 100;
        if (arrear > 0) {
          breakup.push({
            head_code: c.head_code || 'BASIC',
            head_name: c.head_name || c.head_code,
            monthly,
            dailyRate: daily,
            arrear,
          });
          total += arrear;
        }
      });
    } else {
      const gross = Number(compDetails.gross_monthly || compDetails.ctc_monthly || 0);
      if (gross > 0) {
        const daily = gross / baseDays;
        const arrear = Math.round(daily * days * 100) / 100;
        breakup.push({
          head_code: 'BASIC',
          head_name: 'Basic Salary',
          monthly: gross,
          dailyRate: daily,
          arrear,
        });
        total = arrear;
      }
    }

    return { components: breakup, total: Math.round(total * 100) / 100, baseDays };
  }, [selectedEmployeeObj, lopForm.lop_reversal_days, lopForm.effective_year, lopForm.effective_month]);

  // Filtered LOP list (search within selected month)
  const filteredLopList = useMemo(() => {
    let result = [...lopList];

    if (gridSearchQuery.trim()) {
      const q = gridSearchQuery.toLowerCase().trim();
      result = result.filter((r) => {
        const code = (r.employee_code || '').toLowerCase();
        const name = (r.employee_name || '').toLowerCase();
        return code.includes(q) || name.includes(q);
      });
    }

    if (filterStatus) {
      result = result.filter((r) => (r.status || 'pending').toLowerCase() === filterStatus.toLowerCase());
    }

    return result;
  }, [lopList, gridSearchQuery, filterStatus]);

  // Computed summary metrics for selected month
  const summaryMetrics = useMemo(() => {
    if (lopSummary) return lopSummary;
    const totalEmp = new Set(filteredLopList.map((r) => r.employee_id).filter(Boolean)).size;
    const totalDays = filteredLopList.reduce((acc, r) => acc + Number(r.lop_reversal_days || 0), 0);
    const totalAmount = filteredLopList.reduce((acc, r) => acc + Number(r.total_reversal_amount || 0), 0);
    const pendingCount = filteredLopList.filter((r) => (r.status || 'pending') === 'pending').length;
    const processedCount = filteredLopList.filter((r) => r.status === 'processed').length;

    return {
      payroll_month: filterMonth ? formatPeriodMonth(filterMonth) : 'All Months',
      total_employees: totalEmp,
      total_reversal_days: Math.round(totalDays * 100) / 100,
      total_reversal_amount: Math.round(totalAmount * 100) / 100,
      pending_count: pendingCount,
      processed_count: processedCount,
    };
  }, [filteredLopList, lopSummary, filterMonth]);

  const runAutoCalc = async (persist = false) => {
    if (!calc.employee_id || !calc.new_ctc_annual || !calc.effective_from || !calc.effective_to) {
      toast.error('Fill employee, new CTC, effective_from, effective_to');
      return;
    }
    setCalculating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/payroll/arrears/auto-calculate`,
        { ...calc, new_ctc_annual: Number(calc.new_ctc_annual), create: persist },
        { withCredentials: true }
      );
      setCalcResult(data);
      if (persist) {
        toast.success(`Arrear created — ₹${data.total_arrears?.toLocaleString()} for ${data.months_count} months`);
        fetchAll();
      } else {
        toast.success('Calculated — review and click Save to persist');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setCalculating(false);
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API_URL}/payroll/arrears/template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'arrears_bulk_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Template download failed');
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/payroll/arrears/bulk-upload`, fd, {
        withCredentials: true,
      });
      setUploadResult(data);
      toast.success(`Uploaded — ${data.created} created, ${data.skipped} skipped`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  // ─── Save / Update / Delete LOP Reversal ──────────────────────────────
  const saveLopReversal = async () => {
    if (
      !lopForm.employee_id ||
      !lopForm.source_year ||
      !lopForm.source_month ||
      !lopForm.effective_year ||
      !lopForm.effective_month ||
      !lopForm.processing_year ||
      !lopForm.processing_month ||
      !lopForm.lop_reversal_days ||
      !lopForm.approved_by ||
      lopForm.approved_by.toLowerCase() === 'not assigned'
    ) {
      toast.error('Employee, Source, Effective, Processing months, Days, and Approved By role selection are required');
      return;
    }

    setSavingLop(true);
    try {
      const payload = {
        employee_id: lopForm.employee_id,
        source_year: Number(lopForm.source_year),
        source_month: Number(lopForm.source_month),
        effective_year: Number(lopForm.effective_year),
        effective_month: Number(lopForm.effective_month),
        processing_year: Number(lopForm.processing_year),
        processing_month: Number(lopForm.processing_month),
        lop_reversal_days: Number(lopForm.lop_reversal_days),
        reason: lopForm.reason,
        remarks: lopForm.remarks,
        approved_by: lopForm.approved_by,
        approved_by_role: lopForm.approved_by_role || 'Approver',
      };

      if (editingLop) {
        await axios.put(`${API_URL}/payroll/lop-reversals/${editingLop.id || editingLop._id}`, payload, {
          withCredentials: true,
        });
        toast.success('LOP Reversal record updated successfully');
        setEditingLop(null);
      } else {
        const { data } = await axios.post(`${API_URL}/payroll/lop-reversals`, payload, {
          withCredentials: true,
        });
        toast.success(
          `LOP Reversal saved — ₹${(data.total_reversal_amount || 0).toLocaleString()} (${data.lop_reversal_days} days) for ${data.processing_period}`
        );
      }

      setLopForm({
        employee_id: '',
        source_year: new Date().getFullYear(),
        source_month: new Date().getMonth() + 1,
        effective_year: new Date().getFullYear(),
        effective_month: new Date().getMonth() + 1,
        processing_year: new Date().getFullYear(),
        processing_month: new Date().getMonth() + 1,
        lop_reversal_days: '',
        reason: '',
        remarks: '',
        approved_by: '',
        approved_by_role: '',
      });
      setEmpSearchQuery('');
      setApproverSearchQuery('');
      fetchLopData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSavingLop(false);
  };

  const handleDeleteLop = async (id) => {
    try {
      await axios.delete(`${API_URL}/payroll/lop-reversals/${id}`, { withCredentials: true });
      toast.success('LOP Reversal record deleted successfully');
      setDeletingLopId(null);
      fetchLopData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleEditClick = (rec) => {
    setEditingLop(rec);
    setLopForm({
      employee_id: rec.employee_id,
      source_year: rec.source_year || new Date().getFullYear(),
      source_month: rec.source_month || new Date().getMonth() + 1,
      effective_year: rec.effective_year || rec.source_year || new Date().getFullYear(),
      effective_month: rec.effective_month || rec.source_month || new Date().getMonth() + 1,
      processing_year: rec.processing_year || new Date().getFullYear(),
      processing_month: rec.processing_month || new Date().getMonth() + 1,
      lop_reversal_days: rec.lop_reversal_days || '',
      reason: rec.reason || '',
      remarks: rec.remarks || '',
      approved_by: rec.approved_by || '',
      approved_by_role: rec.approved_by_role || '',
    });
    const emp = employees.find((e) => e.id === rec.employee_id || e._id === rec.employee_id);
    if (emp) {
      setEmpSearchQuery(`${emp.employment_details?.employee_code || ''} — ${emp.personal_details?.first_name || ''}`);
    }
    if (rec.approved_by && rec.approved_by !== 'Not Assigned') {
      setApproverSearchQuery(`${rec.approved_by} — ${rec.approved_by_role || 'Approver'}`);
    } else {
      setApproverSearchQuery('');
    }
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const downloadLopTemplate = async () => {
    try {
      const r = await axios.get(`${API_URL}/payroll/lop-reversals/template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lop_reversal_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Template download failed');
    }
  };

  const onLopUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLopUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/payroll/lop-reversals/bulk-upload`, fd, {
        withCredentials: true,
      });
      setLopUploadResult(data);
      toast.success(`Uploaded — ${data.created} created, ${data.skipped} skipped`);
      fetchLopData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setLopUploading(false);
    e.target.value = '';
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-5" data-testid="arrears-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Arrear &amp; LOP Reversal Workbench</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage LOP Reversals with Effective Month base days, component-wise arrear calculations, month-wise tracking, and role-based approval controls.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          {[
            ['list', 'Existing Arrears', FileText],
            ['calc', 'Auto-Calculate from CTC', Calculator],
            ['bulk', 'Bulk Upload (CSV)', Upload],
            ['lop', 'LOP Reversal', RotateCcw],
          ].map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`arr-tab-${k}`}
              className={`px-4 py-2.5 text-sm font-medium uppercase tracking-wider flex items-center gap-2 ${
                tab === k ? 'text-[#0052FF] border-b-2 border-[#0052FF]' : 'text-slate-500'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Existing Arrears List Tab */}
        {tab === 'list' && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="arr-list-tab">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
              </div>
            ) : list.length === 0 ? (
              <div className="p-12 text-center text-slate-400 text-sm">
                No arrears recorded yet. Use the <strong>Auto-Calculate</strong> or <strong>Bulk Upload</strong> tab to create arrears.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3">Employee</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-left px-4 py-3">Period</th>
                    <th className="text-right px-4 py-3">Old CTC</th>
                    <th className="text-right px-4 py-3">New CTC</th>
                    <th className="text-right px-4 py-3">Total Arrears</th>
                    <th className="text-center px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.map((r, i) => (
                    <tr key={r.id || i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs">{r.employee_code || r.employee_id?.slice(-6)}</td>
                      <td className="px-4 py-3 text-xs">{r.type}</td>
                      <td className="px-4 py-3 text-xs">
                        {r.effective_from} → {r.effective_to}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{(r.old_ctc_annual || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono">{(r.new_ctc_annual || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">
                        {(r.total_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            r.status === 'processed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {r.status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Auto-Calc Tab */}
        {tab === 'calc' && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-3xl space-y-4" data-testid="arr-calc-tab">
            <p className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-3">
              Pick an employee, enter the <strong>new CTC</strong>, the <strong>effective period</strong>, and we&apos;ll compute month-by-month arrears.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Employee</label>
                <select
                  data-testid="arr-emp-select"
                  value={calc.employee_id}
                  onChange={(e) => setCalc({ ...calc, employee_id: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">— Select employee —</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>{`${e.employment_details?.employee_code} — ${e.personal_details?.first_name} ${e.personal_details?.last_name}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">New CTC (Annual ₹)</label>
                <input
                  data-testid="arr-newctc"
                  type="number"
                  value={calc.new_ctc_annual}
                  onChange={(e) => setCalc({ ...calc, new_ctc_annual: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Effective From</label>
                <input
                  data-testid="arr-from"
                  type="date"
                  value={calc.effective_from}
                  onChange={(e) => setCalc({ ...calc, effective_from: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Effective To</label>
                <input
                  data-testid="arr-to"
                  type="date"
                  value={calc.effective_to}
                  onChange={(e) => setCalc({ ...calc, effective_to: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">Reason / Remarks</label>
                <input
                  value={calc.reason}
                  onChange={(e) => setCalc({ ...calc, reason: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="arr-calc-btn"
                onClick={() => runAutoCalc(false)}
                disabled={calculating}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold flex items-center gap-2"
              >
                {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />} Calculate (preview)
              </button>
              <button
                data-testid="arr-save-btn"
                onClick={() => runAutoCalc(true)}
                disabled={calculating || !calcResult}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" /> Save Arrear
              </button>
            </div>
          </div>
        )}

        {/* Bulk Upload Tab */}
        {tab === 'bulk' && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-3xl space-y-4" data-testid="arr-bulk-tab">
            <p className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-3">
              Upload a CSV with columns: <code className="font-mono">employee_code, type, effective_from, effective_to, total_amount, reason</code>.
            </p>
            <div className="flex gap-2">
              <button
                data-testid="arr-tpl-btn"
                onClick={downloadTemplate}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Download Template
              </button>
              <label
                data-testid="arr-upload-label"
                className="px-4 py-2 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-lg text-sm font-semibold flex items-center gap-2 cursor-pointer"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload CSV
                <input data-testid="arr-upload-input" type="file" accept=".csv" className="hidden" onChange={onUpload} disabled={uploading} />
              </label>
            </div>
          </div>
        )}

        {/* ─── LOP REVERSAL ENHANCED TAB ──────────────────────────────────────── */}
        {tab === 'lop' && (
          <div className="space-y-6" data-testid="arr-lop-tab">
            {/* Top Form Box */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 shadow-sm">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <div>
                  <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                    <RotateCcw className="w-5 h-5 text-blue-600" />
                    {editingLop ? 'Edit LOP Reversal Entry' : 'Create New LOP Reversal Entry'}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Component-wise arrear amounts are calculated dynamically from the Effective Month salary structure and base days.
                  </p>
                </div>
                {editingLop && (
                  <button
                    onClick={() => {
                      setEditingLop(null);
                      setLopForm({
                        employee_id: '',
                        source_year: new Date().getFullYear(),
                        source_month: new Date().getMonth() + 1,
                        effective_year: new Date().getFullYear(),
                        effective_month: new Date().getMonth() + 1,
                        processing_year: new Date().getFullYear(),
                        processing_month: new Date().getMonth() + 1,
                        lop_reversal_days: '',
                        reason: '',
                        remarks: '',
                        approved_by: '',
                        approved_by_role: '',
                      });
                      setEmpSearchQuery('');
                      setApproverSearchQuery('');
                    }}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-700 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" /> Cancel Edit Mode
                  </button>
                )}
              </div>

              {/* Form Input Controls */}
              <div className="grid grid-cols-3 gap-4">
                {/* Intelligent Searchable Employee Selector */}
                <div className="col-span-3 relative" ref={searchRef}>
                  <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                    <UserCheck className="w-3.5 h-3.5 text-blue-600" /> Select Employee (Search by Code, Name, Mobile, Dept) <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Type employee code, name, or department..."
                      value={empSearchQuery}
                      onFocus={() => setIsEmpSearchOpen(true)}
                      onChange={(e) => {
                        setEmpSearchQuery(e.target.value);
                        setIsEmpSearchOpen(true);
                      }}
                      className="w-full pl-9 pr-8 py-2 border-2 border-slate-200 rounded-lg text-xs font-medium focus:border-blue-500 outline-none"
                    />
                    {empSearchQuery && (
                      <button
                        onClick={() => {
                          setEmpSearchQuery('');
                          setLopForm((prev) => ({ ...prev, employee_id: '' }));
                        }}
                        className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Dropdown popup */}
                  {isEmpSearchOpen && (
                    <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-100">
                      {filteredEmployees.length === 0 ? (
                        <div className="p-3 text-xs text-slate-400 text-center">No active employees matching "{empSearchQuery}"</div>
                      ) : (
                        filteredEmployees.map((emp) => {
                          const code = emp.employment_details?.employee_code || 'N/A';
                          const name = `${emp.personal_details?.first_name || ''} ${emp.personal_details?.last_name || ''}`.trim() || emp.name || '';
                          const dept = emp.employment_details?.department || '';
                          const desig = emp.employment_details?.designation || '';
                          const isSelected = lopForm.employee_id === emp.id || lopForm.employee_id === emp._id;

                          return (
                            <div
                              key={emp.id || emp._id}
                              onClick={() => {
                                setLopForm((prev) => ({ ...prev, employee_id: emp.id || emp._id }));
                                setEmpSearchQuery(`${code} — ${name}`);
                                setIsEmpSearchOpen(false);
                              }}
                              className={`p-2.5 hover:bg-blue-50 cursor-pointer text-xs flex items-center justify-between transition-colors ${
                                isSelected ? 'bg-blue-50/80 font-bold' : ''
                              }`}
                            >
                              <div>
                                <span className="font-bold font-mono text-slate-900">{code}</span>
                                <span className="mx-2 text-slate-400">|</span>
                                <span className="font-semibold text-slate-800">{name}</span>
                              </div>
                              <div className="text-[10px] text-slate-500 font-mono">
                                {dept} {desig ? `• ${desig}` : ''}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {/* Source Month */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    LOP Source Month <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="month"
                    value={`${lopForm.source_year}-${String(lopForm.source_month).padStart(2, '0')}`}
                    onChange={(e) => {
                      if (e.target.value) {
                        const [y, m] = e.target.value.split('-');
                        const numY = Number(y);
                        const numM = Number(m);
                        setLopForm((prev) => ({
                          ...prev,
                          source_year: numY,
                          source_month: numM,
                          effective_year: numY,
                          effective_month: numM,
                          processing_year: numY,
                          processing_month: numM,
                        }));
                      }
                    }}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-1.5 text-xs font-mono"
                  />
                  <span className="text-[10px] text-slate-400">Month LOP originally deducted</span>
                </div>

                {/* LOP Reversal Effective Month */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Effective Month (Mandatory) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="month"
                    value={`${lopForm.effective_year}-${String(lopForm.effective_month).padStart(2, '0')}`}
                    onChange={(e) => {
                      if (e.target.value) {
                        const [y, m] = e.target.value.split('-');
                        setLopForm((prev) => ({ ...prev, effective_year: Number(y), effective_month: Number(m) }));
                      }
                    }}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-1.5 text-xs font-mono border-blue-300 bg-blue-50/20"
                  />
                  <span className="text-[10px] text-slate-400">Base days &amp; salary structure source</span>
                </div>

                {/* Payroll Processing Month */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Payroll Processing Month <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="month"
                    value={`${lopForm.processing_year}-${String(lopForm.processing_month).padStart(2, '0')}`}
                    onChange={(e) => {
                      if (e.target.value) {
                        const [y, m] = e.target.value.split('-');
                        setLopForm((prev) => ({ ...prev, processing_year: Number(y), processing_month: Number(m) }));
                      }
                    }}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-1.5 text-xs font-mono"
                  />
                  <span className="text-[10px] text-slate-400">Month reversal will be paid</span>
                </div>

                {/* Reversal Days */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    LOP Reversal Days <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    placeholder="e.g. 2.0"
                    value={lopForm.lop_reversal_days}
                    onChange={(e) => setLopForm({ ...lopForm, lop_reversal_days: e.target.value })}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-1.5 text-xs font-mono"
                  />
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Reason</label>
                  <input
                    value={lopForm.reason}
                    onChange={(e) => setLopForm({ ...lopForm, reason: e.target.value })}
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-1.5 text-xs"
                    placeholder="e.g. Approved leave marked LOP"
                  />
                </div>

                {/* Approved By - Searchable Role Dropdown */}
                <div className="relative" ref={approverSearchRef}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Approved By (Role Selection) <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search approver by name, role (HR/Payroll Manager)..."
                      value={approverSearchQuery}
                      onFocus={() => setIsApproverSearchOpen(true)}
                      onChange={(e) => {
                        setApproverSearchQuery(e.target.value);
                        setIsApproverSearchOpen(true);
                      }}
                      className="w-full pl-9 pr-8 py-2 border-2 border-slate-200 rounded-lg text-xs font-medium focus:border-blue-500 outline-none"
                    />
                    {approverSearchQuery && (
                      <button
                        onClick={() => {
                          setApproverSearchQuery('');
                          setLopForm((prev) => ({ ...prev, approved_by: '', approved_by_role: '' }));
                        }}
                        className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {isApproverSearchOpen && (
                    <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-100">
                      {filteredApprovers.length === 0 ? (
                        <div className="p-3 text-xs text-slate-400 text-center">No approver roles matching "{approverSearchQuery}"</div>
                      ) : (
                        filteredApprovers.map((appr) => (
                          <div
                            key={appr.id || appr.user_id}
                            onClick={() => {
                              setLopForm((prev) => ({
                                ...prev,
                                approved_by: appr.employee_name,
                                approved_by_role: appr.role_name,
                              }));
                              setApproverSearchQuery(`${appr.employee_name} — ${appr.role_name}`);
                              setIsApproverSearchOpen(false);
                            }}
                            className="p-2.5 hover:bg-blue-50 cursor-pointer text-xs flex items-center justify-between transition-colors"
                          >
                            <div>
                              <div className="font-bold text-slate-900">{appr.employee_name}</div>
                              <div className="text-[10px] text-blue-600 font-semibold">{appr.role_name}</div>
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono">{appr.department || appr.employee_code}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Real-time Component-wise Arrear Preview Box */}
              {lopPreviewBreakup.components.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between items-center text-xs font-bold text-slate-800">
                    <span>Component-wise Estimated LOP Reversal Breakdown (Base Days: {lopPreviewBreakup.baseDays})</span>
                    <span className="font-mono text-emerald-700 text-sm">Total Reversal: ₹ {lopPreviewBreakup.total.toLocaleString()}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 pt-2 border-t border-slate-200 text-xs">
                    {lopPreviewBreakup.components.map((c) => (
                      <div key={c.head_code} className="bg-white p-2 border border-slate-200 rounded-lg shadow-2xs">
                        <div className="text-[10px] text-slate-500 font-semibold">{c.head_name}</div>
                        <div className="text-xs font-mono font-bold text-slate-900 mt-0.5">₹ {c.arrear.toLocaleString()}</div>
                        <div className="text-[9px] text-slate-400 font-mono">({c.dailyRate.toFixed(1)}/day)</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  data-testid="save-lop-btn"
                  onClick={saveLopReversal}
                  disabled={savingLop}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-2 disabled:opacity-50 shadow-sm"
                >
                  {savingLop ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {editingLop ? 'Update LOP Reversal Entry' : 'Save LOP Reversal Entry'}
                </button>
              </div>
            </div>

            {/* CSV Bulk Upload Section */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3 shadow-sm">
              <p className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <strong>Bulk CSV Upload Support:</strong> Upload columns: <code className="font-mono">employee_code, source_year, source_month, effective_year, effective_month, processing_year, processing_month, lop_reversal_days, reason, remarks</code>
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="lop-tpl-btn"
                  onClick={downloadLopTemplate}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold flex items-center gap-2"
                >
                  <Download className="w-4 h-4" /> Download Updated Template
                </button>
                <label
                  data-testid="lop-upload-label"
                  className="px-4 py-2 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-lg text-xs font-semibold flex items-center gap-2 cursor-pointer"
                >
                  {lopUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload CSV
                  <input data-testid="lop-upload-input" type="file" accept=".csv" className="hidden" onChange={onLopUpload} disabled={lopUploading} />
                </label>
              </div>
            </div>

            {/* 📊 Month-wise Summary Stats Banner */}
            <div className="bg-slate-900 text-white rounded-xl p-5 shadow-lg space-y-3">
              <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-blue-400 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  Month Summary Banner — Selected Month: <span className="text-white font-mono">{summaryMetrics.payroll_month}</span>
                </div>
                <span className="text-[10px] bg-blue-500/20 text-blue-300 font-bold px-2 py-0.5 rounded border border-blue-500/30">
                  Strict Month Scoped
                </span>
              </div>
              <div className="grid grid-cols-5 gap-4 text-center">
                <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700">
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">Total Employees</div>
                  <div className="text-lg font-bold text-white mt-0.5">{summaryMetrics.total_employees}</div>
                </div>
                <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700">
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">Total LOP Days</div>
                  <div className="text-lg font-bold text-blue-400 font-mono mt-0.5">{summaryMetrics.total_reversal_days}</div>
                </div>
                <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700">
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">Total Reversal Amount</div>
                  <div className="text-lg font-bold text-emerald-400 font-mono mt-0.5">₹ {summaryMetrics.total_reversal_amount.toLocaleString()}</div>
                </div>
                <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700">
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">Pending Count</div>
                  <div className="text-lg font-bold text-amber-400 mt-0.5">{summaryMetrics.pending_count}</div>
                </div>
                <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700">
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">Processed Count</div>
                  <div className="text-lg font-bold text-emerald-400 mt-0.5">{summaryMetrics.processed_count}</div>
                </div>
              </div>
            </div>

            {/* Recent Month-wise LOP Reversal Entries Grid */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 shadow-sm">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    Payroll Month LOP Reversal Records ({filteredLopList.length})
                  </h3>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Payroll Month Filter */}
                  <div className="flex items-center gap-1 text-xs">
                    <Filter className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-bold text-slate-700">Payroll Month:</span>
                    <input
                      type="month"
                      value={filterMonth}
                      onChange={(e) => setFilterMonth(e.target.value)}
                      className="border-2 border-blue-400 font-bold rounded px-2.5 py-1 text-xs font-mono bg-blue-50/30 text-blue-900"
                    />
                  </div>

                  {/* Search within Selected Month */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search Code or Name in month..."
                      value={gridSearchQuery}
                      onChange={(e) => setGridSearchQuery(e.target.value)}
                      className="pl-8 pr-3 py-1 border border-slate-300 rounded text-xs w-48"
                    />
                    {gridSearchQuery && (
                      <button
                        onClick={() => setGridSearchQuery('')}
                        className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-1 text-xs">
                    <span className="font-semibold text-slate-600">Status:</span>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="processed">Processed</option>
                    </select>
                  </div>
                </div>
              </div>

              {filteredLopList.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-xs">
                  No LOP Reversal records found for Payroll Processing Month <strong>{filterMonth || 'all'}</strong>.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="lop-list-table">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2.5">Employee Code</th>
                        <th className="text-left px-3 py-2.5">Employee Name</th>
                        <th className="text-left px-3 py-2.5">Payroll Processing Month</th>
                        <th className="text-left px-3 py-2.5">LOP Source Month</th>
                        <th className="text-left px-3 py-2.5">Effective Month</th>
                        <th className="text-right px-3 py-2.5">LOP Days</th>
                        <th className="text-center px-3 py-2.5">Status</th>
                        <th className="text-center px-3 py-2.5">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredLopList.map((r) => {
                        const isProcessed = r.status === 'processed';
                        const procStr = formatPeriodMonth(r.processing_period, r.processing_year, r.processing_month);
                        const srcStr = formatPeriodMonth(null, r.source_year, r.source_month);
                        const effStr = formatPeriodMonth(null, r.effective_year || r.source_year, r.effective_month || r.source_month);

                        return (
                          <tr key={r.id || r._id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-3 py-2.5 font-mono font-bold text-slate-900">{r.employee_code || '—'}</td>
                            <td className="px-3 py-2.5 font-semibold text-slate-800">{r.employee_name || 'Employee'}</td>
                            <td className="px-3 py-2.5 font-mono font-bold text-slate-900">{procStr}</td>
                            <td className="px-3 py-2.5 font-mono text-slate-600">{srcStr}</td>
                            <td className="px-3 py-2.5 font-mono font-medium text-blue-700">{effStr}</td>
                            <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-800">{r.lop_reversal_days}</td>
                            <td className="px-3 py-2.5 text-center">
                              <span
                                className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                                  r.status === 'processed'
                                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                    : r.status === 'approved'
                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                    : 'bg-amber-100 text-amber-700 border border-amber-300'
                                }`}
                              >
                                {r.status || 'pending'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                <button
                                  title="View Component Breakup"
                                  onClick={() => setViewingLop(r)}
                                  className="p-1 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                {r.status !== 'cancelled' && r.is_active !== false ? (
                                  <>
                                    <button
                                      title="Edit Record"
                                      onClick={() => handleEditClick(r)}
                                      className="p-1 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                                    >
                                      <Edit3 className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      title="Delete Record"
                                      onClick={() => setDeletingLopId(r.id || r._id)}
                                      className="p-1 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </>
                                ) : (
                                  <span className="text-[10px] text-slate-400 italic">Cancelled</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* View Modal for Component-wise Breakup */}
        {viewingLop && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full p-5 space-y-4 shadow-xl border border-slate-200">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  LOP Reversal Component Breakdown — {viewingLop.employee_name} ({viewingLop.employee_code})
                </h3>
                <button onClick={() => setViewingLop(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs space-y-2 font-mono bg-slate-50 p-3 rounded-lg">
                <div>Payroll Processing Month: {formatPeriodMonth(viewingLop.processing_period, viewingLop.processing_year, viewingLop.processing_month)}</div>
                <div>LOP Source Month: {formatPeriodMonth(null, viewingLop.source_year, viewingLop.source_month)}</div>
                <div>Effective Month: {formatPeriodMonth(null, viewingLop.effective_year || viewingLop.source_year, viewingLop.effective_month || viewingLop.source_month)}</div>
                <div>LOP Days: {viewingLop.lop_reversal_days}</div>
                <div>Approved By: {viewingLop.approved_by || 'Not Assigned'} ({viewingLop.approved_by_role || 'Approver'})</div>
                <div>Reason: {viewingLop.reason || 'N/A'}</div>
              </div>
              <div>
                <h4 className="text-xs font-bold text-slate-700 mb-2">Component-wise Arrears</h4>
                {viewingLop.component_breakup && viewingLop.component_breakup.length > 0 ? (
                  <table className="w-full text-xs border border-slate-200">
                    <thead className="bg-slate-100 text-[10px] uppercase font-bold text-slate-600">
                      <tr>
                        <th className="p-2 text-left">Component Head</th>
                        <th className="p-2 text-right">Monthly (₹)</th>
                        <th className="p-2 text-right">Arrear (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {viewingLop.component_breakup.map((c, idx) => (
                        <tr key={idx}>
                          <td className="p-2 font-medium">{c.head_name}</td>
                          <td className="p-2 text-right font-mono">{(c.monthly_amount || 0).toLocaleString()}</td>
                          <td className="p-2 text-right font-mono font-bold text-emerald-700">{(c.arrear_amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr className="bg-emerald-50 font-bold">
                        <td className="p-2">Total Reversal Arrear</td>
                        <td></td>
                        <td className="p-2 text-right font-mono text-emerald-800">₹ {(viewingLop.total_reversal_amount || 0).toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <p className="text-xs text-slate-400">Standard single-line cumulative reversal.</p>
                )}
              </div>
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setViewingLop(null)}
                  className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingLopId && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-sm w-full p-5 space-y-4 shadow-xl border border-slate-200 text-center">
              <Trash2 className="w-10 h-10 text-rose-500 mx-auto" />
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Delete LOP Reversal Record?</h3>
                <p className="text-xs text-slate-500 mt-1">This record will be permanently deleted from the database.</p>
              </div>
              <div className="flex justify-center gap-3 pt-2">
                <button
                  onClick={() => setDeletingLopId(null)}
                  className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteLop(deletingLopId)}
                  className="px-4 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold"
                >
                  Confirm Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ArrearsRevision;
